import type { ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';

/**
 * The line under a form field: its error, or its hint, or nothing.
 *
 * Every input in `components/ui/` ended the same way — the same `role="alert"`
 * paragraph, the same icon at the same size, the same fallback to a hint — and
 * six copies of it is six places to get an accessibility detail wrong once and
 * not everywhere (CLAUDE.md §3).
 *
 * The error wins over the hint deliberately: once a field is wrong, what it
 * needs is the correction, and a screen reader announcing both would bury it.
 * That is also why `aria-invalid` on the input skips the hint — the two
 * decisions have to agree, so they are made in one place.
 */
export interface FieldNoteProps {
  /** Shown when set, in place of the hint. */
  error?: string;
  hint?: ReactNode;
  /**
   * Ids the field points at with `aria-describedby`. Optional: a fieldset that
   * labels itself needs no id here, and rendering an empty one would be worse
   * than rendering none.
   */
  errorId?: string;
  hintId?: string;
}

export function FieldNote({ error, hint, errorId, hintId }: FieldNoteProps) {
  if (error) {
    return (
      <p id={errorId} role="alert" className="flex items-center gap-1.5 text-xs text-crit">
        <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {error}
      </p>
    );
  }

  if (hint) {
    return (
      <p id={hintId} className="text-xs text-ink-2 whitespace-pre-line">
        {hint}
      </p>
    );
  }

  return null;
}
